import { createContext, useContext, ReactNode } from 'react';
import { AiReceptionistEventType } from '@weave/schema-gen-ts/dist/shared/phone/events/vair/v1/vair.pb';
import { useTranslation, Trans, i18next } from '@frontend/i18n';
import { AI_EVENT_BADGE_TYPE, AiEventIcon, EventLabelConfig } from '@frontend/phone-ui';
import { openSchedulePulse, SchedulePulseTab } from '@frontend/schedule-helpers';
import { Button, PopoverDialog, usePopoverDialog, Text, TextLink } from '@frontend/design-system';
import { useAIEventGroups } from './use-ai-event-groups';

const PopoverCloseContext = createContext<(() => void) | null>(null);
const usePopoverClose = () => useContext(PopoverCloseContext);

const SchedulePulseLink = () => {
  const close = usePopoverClose();

  return (
    <TextLink
      color='primary'
      onClick={() => {
        close?.();
        openSchedulePulse(SchedulePulseTab.AppointmentRequests);
      }}
      trackingId='schedule-pulse-requests-link'
    >
      {i18next.t('Requests in Schedule Pulse', { ns: 'calls' })}
    </TextLink>
  );
};

export const AiEventDescriptionModalTrigger = ({ trackingId }: { trackingId?: string }) => {
  const { t } = useTranslation('calls');
  const { getTriggerProps, getDialogProps, close } = usePopoverDialog<HTMLButtonElement>({
    initialFocus: -1,
  });
  const { showAppointmentRequestAiEvents, showDirectSchedulingAiEvents } = useAIEventGroups();

  const descriptions = getAiEventDescriptions({ showAppointmentRequestAiEvents, showDirectSchedulingAiEvents });

  return (
    <>
      <Button
        {...getTriggerProps()}
        data-testid='ai-event-description-trigger'
        iconName='info'
        variant='secondary'
        css={{ padding: 0, height: 16, width: 16, svg: { color: 'var(--fabric-color-neutral-50)' } }}
        trackingId={trackingId}
      />
      <PopoverDialog {...getDialogProps()}>
        <PopoverCloseContext.Provider value={close}>
          <Trans t={t}>
            <div style={{ display: 'grid', gap: 'var(--fabric-space-padding-xs)', width: 370 }}>
              <Text size='large' weight='bold'>
                AI Receptionist Outcomes
              </Text>

              <div>
                <Text size='medium' weight='bold'>
                  What They Help With
                </Text>
                <Text size='medium'>
                  See how calls are being handled by your AI Receptionist and whether patients may still need
                  assistance.
                </Text>
              </div>
              <div style={{ display: 'grid', gap: 'var(--fabric-space-padding-xs)' }}>
                <Text size='medium' weight='bold'>
                  How They're Defined
                </Text>
                <div style={{ display: 'grid', gap: 'var(--fabric-space-padding-xs)' }}>
                  {descriptions.map((config) => (
                    <EventSection key={config.event} config={config} />
                  ))}
                </div>
              </div>
            </div>
          </Trans>
        </PopoverCloseContext.Provider>
      </PopoverDialog>
    </>
  );
};

type AiEventDescription = {
  event: EventLabelConfig['event'];
  text: ReactNode;
  secondaryText?: ReactNode;
};

const AI_EVENT_DESCRIPTIONS: Record<string, AiEventDescription> = {
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_MISSED]: {
    event: AI_EVENT_BADGE_TYPE.TRANSFER_MISSED,
    text: i18next.t('Transfer Missed', { ns: 'calls' }),
    secondaryText: i18next.t(
      "The AI Receptionist transferred the call and the office staff didn't answer or it went to voicemail. Transfers can happen when the caller’s request isn't supported or when the caller asks to speak with staff. The caller may still need assistance. ",
      { ns: 'calls' }
    ),
  },
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_ANSWERED]: {
    event: AI_EVENT_BADGE_TYPE.TRANSFERRED_TO_STAFF,
    text: i18next.t('Transferred to Staff', { ns: 'calls' }),
    secondaryText: i18next.t(
      'The AI Receptionist transferred the call to your office staff and they answered the call.',
      { ns: 'calls' }
    ),
  },
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_ADDED]: {
    event: AI_EVENT_BADGE_TYPE.SCHEDULE_ADDED,
    text: i18next.t('Added to Schedule', { ns: 'calls' }),
    secondaryText: i18next.t('The AI Receptionist scheduled an appointment for the patient.', { ns: 'calls' }),
  },
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_CANCELLED]: {
    event: AI_EVENT_BADGE_TYPE.SCHEDULE_CANCELLED,
    text: i18next.t('Cancelled', { ns: 'calls' }),
    secondaryText: i18next.t('The AI Receptionist cancelled an appointment for the patient.', { ns: 'calls' }),
  },
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_CONFIRMED]: {
    event: AI_EVENT_BADGE_TYPE.SCHEDULE_CONFIRMED,
    text: i18next.t('Confirmed', { ns: 'calls' }),
    secondaryText: i18next.t('The AI Receptionist confirmed an appointment for the patient.', { ns: 'calls' }),
  },
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_RESCHEDULED]: {
    event: AI_EVENT_BADGE_TYPE.SCHEDULE_RESCHEDULED,
    text: i18next.t('Rescheduled', { ns: 'calls' }),
    secondaryText: i18next.t('The AI Receptionist rescheduled an appointment for the patient.', { ns: 'calls' }),
  },
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_REQUESTED]: {
    event: AI_EVENT_BADGE_TYPE.SCHEDULE_REQUESTED,
    text: i18next.t('Appointment Requested', { ns: 'calls' }),
    secondaryText: (
      <>
        {i18next.t(
          'The AI Receptionist created an Appointment Request for the patient. Review your requests and follow up with patients under',
          { ns: 'calls' }
        )}{' '}
        <SchedulePulseLink />.
      </>
    ),
  },
  [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_FATAL]: {
    event: AI_EVENT_BADGE_TYPE.ERROR,
    text: i18next.t('Error', { ns: 'calls' }),
    secondaryText: i18next.t(
      'The AI Receptionist could not answer the call or the call was disrupted. This can happen when the set up is incomplete or when the system is experiencing a temporary disruption.',
      { ns: 'calls' }
    ),
  },
};

// Always-visible outcomes shown regardless of the active AI flags.
const COMMON_AI_EVENT_TYPES = [
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_MISSED,
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_ANSWERED,
];

const INTEGRATION_AI_EVENT_TYPES = [
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_ADDED,
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_CANCELLED,
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_CONFIRMED,
  AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_RESCHEDULED,
];

const VOICE_AI_EVENT_TYPES = [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_SCHEDULE_REQUESTED];

const FATAL_AI_EVENT_TYPES = [AiReceptionistEventType.AI_RECEPTIONIST_EVENT_TYPE_CALL_TRANSFER_FATAL];

const getAiEventDescriptions = ({
  showAppointmentRequestAiEvents,
  showDirectSchedulingAiEvents,
}: {
  showAppointmentRequestAiEvents: boolean;
  showDirectSchedulingAiEvents: boolean;
}): AiEventDescription[] =>
  [
    ...COMMON_AI_EVENT_TYPES,
    ...(showDirectSchedulingAiEvents ? INTEGRATION_AI_EVENT_TYPES : []),
    ...(showAppointmentRequestAiEvents ? VOICE_AI_EVENT_TYPES : []),
    ...FATAL_AI_EVENT_TYPES,
  ].map((type) => AI_EVENT_DESCRIPTIONS[type]);

const EventSection = ({ config }: { config: AiEventDescription }) => (
  <section
    style={{
      display: 'grid',
      gridTemplateColumns: 'auto 1fr',
      alignItems: 'center',
      gap: 'var(--fabric-space-padding-xxs)',
    }}
  >
    <div
      style={{
        justifySelf: 'end',
        alignSelf: 'start',
        display: 'grid',
        marginTop: 'var(--fabric-space-padding-xxs)',
      }}
    >
      <AiEventIcon event={config.event} />
    </div>
    <div>
      <Text as='span' size='medium' weight='bold'>
        {config.text}:{' '}
      </Text>
      {config.secondaryText && (
        <Text as='span' size='medium'>
          {config.secondaryText}
        </Text>
      )}
    </div>
  </section>
);
